import { ListItemCheckmark } from '@euler/app/components/list/ListItemCheckmark';
import { OrderNavParams } from '@euler/app/flows/order/OrderFlow';
import { AddVehicleInfoHeaderItem } from '@euler/app/flows/order/components/AddVehicleInfoHeaderItem';
import { AlphabetIndex } from '@euler/app/flows/order/components/AlphabetIndex';
import { Separator } from '@euler/app/flows/order/components/Separator';
import { useVehicleBrandList } from '@euler/app/flows/order/functions/useVehicleBrandList';
import { Avatar } from '@euler/components/Avatar';
import { Center } from '@euler/components/Center';
import { AdvancedImage } from '@euler/components/image/AdvancedImage';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { VehicleBrandInfo, VehicleModelInfo } from '@euler/model/vehicle';
import getSectionListItemLayout from '@euler/utils/getSectionListItemLayout';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  SectionList,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';

type Item = VehicleBrandInfo & {
  first?: boolean;
  last?: boolean;
  initial: string;
};

type Section = { title: string; data: Item[] };

const kSectionHeaderHeight = 25;
const kItemHeight = 56;

export const VehicleBrandSelectorScreen = wrapNavigatorScreen(
  ({
    selected,
    mode = 'brand-with-model',
    withCustomModel = true,
    onSelect,
    onSelectBrand,
  }: {
    selected?: {
      brand: string | undefined;
      model: string | undefined;
    };
    mode?: 'brand-only' | 'brand-with-model';
    withCustomModel?: boolean;
    onSelect?: (brand: string, model: VehicleModelInfo) => void;
    onSelectBrand?: (brand: VehicleBrandInfo) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const listRef = useRef<SectionList>(null);
    const navigation = useNavigation<StackNavigationProp<OrderNavParams>>();
    const insets = useSafeAreaInsets();
    const brands = useVehicleBrandList();

    const { sections, indics, count } = useMemo(() => {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const itemLayouts = [];
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const sections: Section[] = [];
      const map = new Map<string, Section>();
      let offset = 0;
      let index = 0;
      for (const brand of brands ?? []) {
        if (!map.has(brand.initial)) {
          map.set(brand.initial, { title: brand.initial, data: [] });
          sections.push(map.get(brand.initial)!);
          itemLayouts.push({
            length: kSectionHeaderHeight,
            offset,
            index: index++,
          });
          offset += kSectionHeaderHeight;
        }
        itemLayouts.push({
          length: kItemHeight,
          offset,
          index: index++,
        });
        offset += kItemHeight;
        map.get(brand.initial)!.data.push(brand);
      }
      return {
        sections,
        itemLayouts,
        indics: sections.map(x => x.title),
        count: offset,
      };
    }, [brands]);

    const [batch, setBatch] = useState(20);

    const onIndexChange = useCallback((index: number) => {
      listRef.current?.scrollToLocation({ sectionIndex: index, itemIndex: 0 });
    }, []);

    const onPress = useCallback(
      (item: Item) => {
        if (mode === 'brand-only') {
          navigation.goBack();
          onSelectBrand?.({
            name: item.name,
            logo: item.logo,
            initial: item.initial,
          });
          return;
        }
        navigation.push('_vehicleModelSelector', {
          brand: item.name,
          selected: selected?.model,
          onSelect: (brand, model) => {
            navigation.goBack();
            onSelect?.(brand, model);
          },
        });
      },
      [mode, navigation, onSelectBrand, onSelect, selected?.model],
    );

    useEffect(() => {
      if (count > 0) {
        setBatch(count);
      }
    }, [count]);

    useEffect(() => {
      if (!withCustomModel) return;
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const onPress = () => {
        navigation.navigate('_addVehicleInfo', {
          onConfirm: (brand, model) => {
            onSelect?.(brand, { name: model, manufacturer: '' });
            navigation.goBack();
          },
        });
      };
      navigation.setOptions({
        headerRight: () => <AddVehicleInfoHeaderItem onPress={onPress} />,
      });
    }, [navigation, onSelect, withCustomModel]);

    if (!brands) {
      return (
        <Center style={{ flex: 1 }}>
          <ActivityIndicator />
        </Center>
      );
    }

    return (
      <>
        <SectionList
          css={`
            flex: 1;
          `}
          ref={listRef}
          contentContainerStyle={{ paddingBottom: insets.bottom + rpx(8) }}
          sections={sections}
          initialNumToRender={batch}
          keyExtractor={(item, index) => item.name + index}
          renderSectionHeader={({ section }) => (
            <SectionHeader text={section.title} />
          )}
          renderItem={({ item }) => (
            <Cell
              item={item}
              selected={item.name === selected?.brand}
              onPress={onPress}
            />
          )}
          getItemLayout={
            getSectionListItemLayout({
              getItemHeight: () => rpx(56),
              getSectionHeaderHeight: () => rpx(25),
              getSeparatorHeight: () => 0,
              getSectionFooterHeight: () => 0,
            }) as any
          }
        />
        <AlphabetIndex indics={indics} onChange={onIndexChange} />
      </>
    );
  },
  { title: '选择品牌' },
);

const SectionHeader = memo(({ text }: { text: string }) => {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  return (
    <View
      css={`
        height: ${rpx(kSectionHeaderHeight)}px;
        padding: 0 ${rpx(15)}px;
        justify-content: center;
        background-color: ${theme.page.background};
      `}
    >
      <Text
        css={`
          font-family: ${FontFamily.NotoSans.Bold};
          font-size: ${rpx(14)}px;
          color: #000;
        `}
      >
        {text}
      </Text>
    </View>
  );
});

const Cell = memo(
  ({
    item,
    selected,
    onPress,
  }: {
    item: Item;
    selected?: boolean;
    onPress?: (item: VehicleBrandInfo) => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const onItemPress = useCallback(() => {
      onPress?.(item);
    }, [item, onPress]);

    return (
      <TouchableOpacity
        onPress={onItemPress}
        css={`
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          background-color: ${theme.components.table.item.backgroundColor};
          height: ${rpx(kItemHeight)}px;
          padding-left: ${rpx(15)}px;
          padding-right: ${rpx(15)}px;
        `}
      >
        {!item.last && (
          <Separator
            css={`
              bottom: 0;
              left: ${rpx(15)}px;
            `}
          />
        )}
        {item.logo ? (
          <AdvancedImage
            uri={item.logo}
            css={`
              width: ${rpx(35)}px;
              height: ${rpx(35)}px;
              margin-right: ${rpx(20)}px;
            `}
          />
        ) : (
          <Avatar
            name={item.name}
            textStyle={{
              fontFamily: FontFamily.NotoSans.Light,
              fontSize: rpx(16),
            }}
            css={`
              width: ${rpx(35)}px;
              height: ${rpx(35)}px;
              border-radius: ${rpx(17.5)}px;
              margin-right: ${rpx(20)}px;
            `}
          />
        )}
        <Text
          css={`
            flex: 1;
            font-family: ${FontFamily.NotoSans.Regular};
            font-size: ${rpx(15)}px;
            color: #000;
          `}
        >
          {item.name}
        </Text>
        <ListItemCheckmark
          checked={selected}
          css={`
            margin-right: ${rpx(24)}px;
          `}
        />
      </TouchableOpacity>
    );
  },
);
